import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Bot, User } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// This is the system context you mentioned in the plan.
// The LLM should be provided this context when making an API call so it knows how to answer.
const SYSTEM_PROMPT = `
You are the Audivis Assistant, an AI trained to help users navigate the Audivis platform.
Audivis is an audit-grade financial intelligence suite built by a Chartered Accountant.
Key features include:
1. Nexus Audit: Enterprise-grade audit management, engagement tracking, PBC requests.
2. LeaseLens v5: Finance-grade lease accounting for Ind AS 116 / IFRS 16, ROU, journal entries.
3. ESOP Valuation: Black-Scholes ESOP & RSU valuation reports, DLOM, PDF exports.
4. FinConsolidate: Multi-entity financial consolidation with Excel-native workflow.
Always be professional, precise, and helpful. If you don't know an answer, direct them to contact support.
`;

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hello! I'm the Audivis Assistant. How can I help you with our financial suite today?" }
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Call the PHP backend endpoint which securely holds the Anthropic API key
  const fetchAIResponse = async (userMessage) => {
    setIsTyping(true);
    
    try {
      const response = await fetch("/api/chatbot.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: [...messages, { role: "user", content: userMessage }]
        })
      });
      
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      
      const data = await response.json();
      return data.reply || "Sorry, I'm having trouble connecting to the server.";
      
    } catch (error) {
      console.error("Chatbot API Error:", error);
      return "Sorry, I encountered an error. Please try again later.";
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = input.trim();
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setInput("");

    const aiResponse = await fetchAIResponse(userMessage);
    
    setIsTyping(false);
    setMessages((prev) => [...prev, { role: "assistant", content: aiResponse }]);
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 p-4 rounded-full bg-[#202A36] text-white shadow-xl transition-all duration-300 hover:scale-105 z-50 ${isOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        aria-label="Open Chat"
      >
        <MessageCircle size={24} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 right-6 w-80 sm:w-96 h-[500px] max-h-[80vh] bg-white rounded-2xl shadow-2xl flex flex-col z-50 border border-gray-200 overflow-hidden"
          >
            {/* Header */}
            <div className="bg-[#202A36] text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bot size={20} className="text-sky-300" />
                <div>
                  <h3 className="font-medium text-sm">Audivis Assistant</h3>
                  <p className="text-[10px] text-white/60 uppercase tracking-widest font-mono">Live Support</p>
                </div>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="text-white/70 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50 flex flex-col gap-4">
              {messages.map((msg, idx) => (
                <div 
                  key={idx} 
                  className={`flex items-start gap-2 max-w-[85%] ${msg.role === 'user' ? 'ml-auto flex-row-reverse' : 'mr-auto'}`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-1 ${msg.role === 'user' ? 'bg-sky-100 text-sky-700' : 'bg-[#202A36] text-white'}`}>
                    {msg.role === 'user' ? <User size={12} /> : <Bot size={12} />}
                  </div>
                  <div 
                    className={`p-3 rounded-2xl text-sm ${msg.role === 'user' ? 'bg-[#202A36] text-white rounded-tr-sm' : 'bg-white text-gray-800 border border-gray-200 shadow-sm rounded-tl-sm'}`}
                  >
                    {msg.content}
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="flex items-center gap-2 max-w-[85%] mr-auto">
                   <div className="w-6 h-6 rounded-full bg-[#202A36] text-white flex items-center justify-center shrink-0">
                    <Bot size={12} />
                  </div>
                  <div className="p-3 rounded-2xl bg-white border border-gray-200 shadow-sm rounded-tl-sm flex gap-1 items-center">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></span>
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <form onSubmit={handleSend} className="p-3 bg-white border-t border-gray-100 flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about Audivis..."
                className="flex-1 bg-gray-50 border border-gray-200 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#202A36]/20 transition-all"
              />
              <button 
                type="submit"
                disabled={!input.trim() || isTyping}
                className="w-10 h-10 rounded-full bg-[#202A36] text-white flex items-center justify-center hover:bg-[#2a3644] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
              >
                <Send size={16} className="ml-1" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
